// CreateGradeModal.jsx
import React, { useEffect, useState } from "react";
import { FaSave, FaTimes } from "react-icons/fa";
import Button from "./../../../components/Button/Button";
import { createGrade } from "../../../services/gradCnServices";
import { getAdherentsWithBrownBeltAndAbove } from "../../../services/beltServices";
import { toast } from "react-toastify";

const initialForm = {
  adherent_id: "",
  dominante: "",
  uv1: false,
  uv2: false,
  uv3_points: "",
  uv3: false,
  uv4: false,
  observations: "",
};

const CreateGradeModal = ({ isOpen, onClose, refreshList }) => {
  const [formValues, setFormValues] = useState(initialForm);
  const [adherents, setAdherents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Chargement des adhérents (marron et +)
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        setLoading(true);
        const list = await getAdherentsWithBrownBeltAndAbove();
        // tri alpha NOM puis PRÉNOM, comme ailleurs
        const sorted = (list || []).slice().sort((a, b) => {
          const an = (a.nom || "").localeCompare(b.nom || "", "fr");
          if (an !== 0) return an;
          return (a.prenom || "").localeCompare(b.prenom || "", "fr");
        });
        setAdherents(sorted);
      } catch (e) {
        console.error(e);
        toast.error("Erreur lors du chargement des adhérents (marron et +).");
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen]);

  // ESC pour fermer
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === "Escape" && handleClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    setFormValues(initialForm);
    onClose?.();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formValues.adherent_id) {
      toast.error("Veuillez sélectionner un adhérent.");
      return;
    }
    if (!formValues.dominante) {
      toast.error("Veuillez sélectionner une dominante.");
      return;
    }

    try {
      setSaving(true);
      await createGrade({
        ...formValues,
        uv3_points: formValues.uv3_points === "" ? 0 : Number(formValues.uv3_points),
      });
      toast.success("Grade créé avec succès.");
      await Promise.resolve(refreshList?.());
      handleClose();
    } catch (error) {
      console.error("Erreur lors de la création du grade", error);
      toast.error("Erreur lors de la création du grade.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden
      />
      {/* Modal */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-[720px] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 flex flex-col max-h-[85vh] overflow-hidden font-montserrat">
          {/* Header dégradé (aligné avec le redesign) */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 flex items-center justify-between rounded-t-2xl">
            <h3 className="text-white font-semibold">Créer un Grade CN</h3>
            <Button
              text="Fermer"
              icon={FaTimes}
              onClick={handleClose}
              className="!rounded-xl bg-white/10 hover:bg-white/20 text-white gap-2"
            />
          </div>

          {/* Body */}
          <div className="p-5 overflow-y-auto">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Sélection adhérent (marron et +) */}
              <div>
                <label className="block text-xs mb-1 opacity-70">Adhérent</label>
                <select
                  name="adherent_id"
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
                  value={formValues.adherent_id}
                  onChange={handleChange}
                  disabled={loading}
                  required
                >
                  <option value="">{loading ? "Chargement..." : "Sélectionnez un adhérent (marron et +)"}</option>
                  {adherents.map((ad) => (
                    <option key={ad.id} value={ad.id}>
                      {ad.nom} {ad.prenom}
                    </option>
                  ))}
                </select>
              </div>

              {/* Dominante */}
              <div>
                <label className="block text-xs mb-1 opacity-70">Dominante</label>
                <select
                  name="dominante"
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
                  value={formValues.dominante}
                  onChange={handleChange}
                  required
                >
                  <option value="">Sélectionnez une dominante</option>
                  <option value="Compétition">Compétition</option>
                  <option value="Technique">Technique</option>
                </select>
              </div>

              {/* Lignes UV (switch/checkbox) */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <label className="inline-flex items-center gap-2 rounded-xl px-3 py-2 bg-slate-50 dark:bg-slate-800/40 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
                  <input
                    type="checkbox"
                    name="uv1"
                    checked={formValues.uv1}
                    onChange={handleChange}
                    className="toggle toggle-primary"
                  />
                  <span>UV1</span>
                </label>

                <label className="inline-flex items-center gap-2 rounded-xl px-3 py-2 bg-slate-50 dark:bg-slate-800/40 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
                  <input
                    type="checkbox"
                    name="uv2"
                    checked={formValues.uv2}
                    onChange={handleChange}
                    className="toggle toggle-primary"
                  />
                  <span>UV2</span>
                </label>

                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-xs mb-1 opacity-70">UV3 Points</label>
                  <input
                    type="number"
                    name="uv3_points"
                    placeholder="0"
                    min="0"
                    value={formValues.uv3_points}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
                    required
                  />
                </div>

                <label className="inline-flex items-center gap-2 rounded-xl px-3 py-2 bg-slate-50 dark:bg-slate-800/40 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
                  <input
                    type="checkbox"
                    name="uv3"
                    checked={formValues.uv3}
                    onChange={handleChange}
                    className="toggle toggle-primary"
                  />
                  <span>UV3</span>
                </label>

                <label className="inline-flex items-center gap-2 rounded-xl px-3 py-2 bg-slate-50 dark:bg-slate-800/40 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
                  <input
                    type="checkbox"
                    name="uv4"
                    checked={formValues.uv4}
                    onChange={handleChange}
                    className="toggle toggle-primary"
                  />
                  <span>UV4</span>
                </label>

                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-xs mb-1 opacity-70">Observations</label>
                  <textarea
                    name="observations"
                    value={formValues.observations}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
                    rows="3"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-center gap-2 pt-2">
                <Button
                  text={saving ? "Enregistrement..." : "Enregistrer"}
                  icon={FaSave}
                  type="submit"
                  disabled={saving}
                  className="!rounded-xl gap-2"
                />
                <Button
                  text="Annuler"
                  icon={FaTimes}
                  onClick={handleClose}
                  type="button"
                  className="!rounded-xl !bg-slate-100 !text-slate-900 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 gap-2"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateGradeModal;
